import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";

const getGallery = (product: any) =>
  Array.from(
    new Set(
      ([product?.image_url, ...(product?.images ?? [])].filter(Boolean) as string[]),
    ),
  );

interface Props {
  product: any;
  /** Kept for backward compatibility — no longer affects images. */
  selectedColor?: string | null;
  activeImage: string | null;
  setActiveImage: (url: string) => void;
}

export const ProductGallery = ({ product, activeImage, setActiveImage }: Props) => {
  const gallery = getGallery(product);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [gallery.length]);

  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <div className="space-y-3">
      <div className="aspect-square overflow-hidden rounded-3xl bg-white border border-border shadow-card flex items-center justify-center p-4">
        {activeImage ? (
          <img
            key={activeImage}
            src={activeImage}
            alt={product.name}
            className="max-h-full max-w-full object-contain animate-fade-in"
          />
        ) : (
          <ShoppingBag className="h-20 w-20 text-muted-foreground/30" />
        )}
      </div>

      {gallery.length > 1 && (
        <div className="relative group">
          {canLeft && (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Scroll thumbnails left"
              className="hidden md:grid absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 place-items-center rounded-full bg-white/95 border border-border shadow-md hover:bg-white transition"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {canRight && (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Scroll thumbnails right"
              className="hidden md:grid absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 place-items-center rounded-full bg-white/95 border border-border shadow-md hover:bg-white transition"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {gallery.map((url) => {
              const isActive = activeImage === url;
              return (
                <button
                  key={url}
                  type="button"
                  onClick={() => setActiveImage(url)}
                  className={`shrink-0 snap-start h-16 w-16 md:h-20 md:w-20 rounded-xl overflow-hidden bg-white border-2 p-1 transition-all ${
                    isActive
                      ? "border-[hsl(24,95%,53%)] ring-2 ring-[hsl(24,95%,53%)]/30"
                      : "border-border hover:border-foreground/20"
                  }`}
                >
                  <img src={url} alt="" className="h-full w-full object-contain" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
